/**
 * @file Анимация раздачи карт для онлайн-белота.
 *
 * Семантика как в локальном `useBelotDealAnimation` + `flyHeroDealCardFromDeck`:
 * триггер в `phase === 'auction'` при полной стартовой руке героя (`cardsPerSeatForDeal`),
 * ключ `${roundIndex}-${sortedHand}`; полёт карты с `await` до завершения, затем
 * reveal слота; refs-поколения и `dealFlightAbortRef` как в локале.
 *
 * Звук и «подавление раздачи при гидрации» разведены: `audioSuppressed` только mute,
 * `suppressDealAnimation` — пропуск всей анимации (реконнект / catch-up).
 *
 * @module components/online/hooks/useBelotOnlineDealAnimation
 */

import { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { PlayingCard } from '../../../poker-ui/PlayingCard';

const DEAL_HERO_BURST_MS = 105;
const DEAL_HERO_STAGGER_MS = 22;
const DEAL_BETWEEN_SEATS_MS = 85;
const DEAL_SEAT_ORDER = [0, 1, 2, 3];
const DEAL_CARD_SOUND_VOL = 0.78;

/** Максимум слотов reveal по месту (4p = 8; open duell старт = 6). */
const REVEAL_SLOTS_PER_SEAT = 8;

/**
 * Тот же полёт, что в `BelotArmenianLocalLobby.jsx` (`flyHeroDealCardFromDeck`).
 *
 * @param {HTMLElement} deckEl
 * @param {HTMLElement} targetEl
 * @param {() => void} onFlightStartSound
 * @param {'sm' | 'md'} [cardSize]
 * @returns {Promise<void>}
 */
function flyHeroDealCardFromDeck(deckEl, targetEl, onFlightStartSound, cardSize = 'md') {
  return new Promise((resolve) => {
    const deckRect = deckEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    if (deckRect.width < 2 || targetRect.width < 2) {
      resolve();
      return;
    }
    onFlightStartSound();
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = `${deckRect.left}px`;
    wrapper.style.top = `${deckRect.top}px`;
    wrapper.style.width = `${deckRect.width}px`;
    wrapper.style.height = `${deckRect.height}px`;
    wrapper.style.zIndex = '120';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.transformOrigin = 'top left';
    document.body.appendChild(wrapper);
    const root = createRoot(wrapper);
    root.render(React.createElement(PlayingCard, { faceDown: true, size: cardSize }));
    requestAnimationFrame(() => {
      try {
        const tr = targetEl.getBoundingClientRect();
        const wr = wrapper.getBoundingClientRect();
        if (wr.width < 1) {
          try {
            root.unmount();
          } catch {
            /* ignore */
          }
          try {
            wrapper.remove();
          } catch {
            /* ignore */
          }
          resolve();
          return;
        }
        const tx = tr.left - wr.left;
        const ty = tr.top - wr.top;
        const sx = tr.width / Math.max(wr.width, 1);
        const sy = tr.height / Math.max(wr.height, 1);
        const anim = wrapper.animate(
          [
            { transform: 'translate(0px, 0px) scale(1, 1)' },
            { transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})` },
          ],
          {
            duration: DEAL_HERO_BURST_MS,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'forwards',
          },
        );
        anim.finished
          .catch(() => {})
          .finally(() => {
            try {
              root.unmount();
            } catch {
              /* ignore */
            }
            try {
              wrapper.remove();
            } catch {
              /* ignore */
            }
            resolve();
          });
      } catch {
        try {
          root.unmount();
        } catch {
          /* ignore */
        }
        try {
          wrapper.remove();
        } catch {
          /* ignore */
        }
        resolve();
      }
    });
  });
}

function sleep(ms) {
  return new Promise((r) => {
    window.setTimeout(r, ms);
  });
}

/**
 * @param {object} args
 * @param {object|null} args.belotArmenian — публичный снапшот (4p или open duell).
 * @param {React.MutableRefObject<HTMLElement|null>} args.deckRef
 * @param {React.MutableRefObject<Record<number, HTMLElement|null>>} args.seatRefs — цель полёта для соперников (контейнер места / аватар).
 * @param {number[]|null|undefined} [args.heroHand] — рука героя для триггера.
 * @param {(soundId: string, volume?: number) => void} args.play
 * @param {boolean} [args.suppressed] — legacy: и звук, и анимация (если не переданы audioSuppressed/suppressDealAnimation).
 * @param {boolean} [args.audioSuppressed] — не вызывать `play` (autoplay / реконнект-звук).
 * @param {boolean} [args.suppressDealAnimation] — не запускать раздачу (гидрация после реконнекта).
 * @param {number} [args.cardsPerSeatForDeal] — 8 (4p) или 6 (open duell 1×1).
 * @param {number|null} [args.heroSeat]
 * @param {Record<number, (import('react').RefObject<HTMLElement|null>)[]|null|undefined>|null|undefined} [args.seatCardSlotRefs] — опционально (open belot); иначе полёт к `seatRefs`.
 * @param {() => () => void} [args.onDealFxLock] — `acquireCritical` очереди: вернуть `release` в `finally` / cleanup после WAAPI.
 */
export function useBelotOnlineDealAnimation({
  belotArmenian,
  deckRef,
  seatRefs,
  handSlotRefs,
  seatCardSlotRefs,
  play,
  suppressed = false,
  audioSuppressed: audioSuppressedProp,
  suppressDealAnimation: suppressDealAnimationProp,
  heroSeat = null,
  heroHand,
  cardsPerSeatForDeal = 8,
  setRoundDealAnimActive,
  setSeatDealSlotRevealed,
  onDealFxLock,
}) {
  const dealAnimGenRef = useRef(0);
  const dealFlightAbortRef = useRef(false);

  const audioSuppressed = audioSuppressedProp !== undefined ? audioSuppressedProp : suppressed;
  const suppressDealAnimation =
    suppressDealAnimationProp !== undefined ? suppressDealAnimationProp : suppressed;

  const audioSuppressedRef = useRef(audioSuppressed);
  audioSuppressedRef.current = audioSuppressed;

  const playDealSound = useMemo(
    () => () => {
      if (!audioSuppressedRef.current) play('card-deal', DEAL_CARD_SOUND_VOL);
    },
    [play],
  );

  const heroDealTriggerKey = (() => {
    if (!belotArmenian || suppressDealAnimation) return '';
    if (String(belotArmenian.phase || '') !== 'auction') return '';
    const raw = heroHand;
    if (!Array.isArray(raw) || raw.length !== cardsPerSeatForDeal) return '';
    const sorted = [...raw].sort((a, b) => a - b);
    return `${Number(belotArmenian.roundIndex ?? 0)}-${sorted.join(',')}`;
  })();

  useEffect(() => {
    if (!belotArmenian) return undefined;
    if (String(belotArmenian.phase || '') !== 'auction') {
      setRoundDealAnimActive?.(false);
      setSeatDealSlotRevealed?.(null);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только `phase`, как в useBelotDealAnimation
  }, [belotArmenian?.phase, setRoundDealAnimActive, setSeatDealSlotRevealed]);

  useLayoutEffect(() => {
    if (!heroDealTriggerKey) return undefined;
    if (suppressDealAnimation) return undefined;

    const gen = ++dealAnimGenRef.current;
    let cancelled = false;
    dealFlightAbortRef.current = false;
    setRoundDealAnimActive?.(true);

    /** @type {(() => void) | null} */
    let dealReleaseRef = null;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    const totalDealCards = Math.max(1, cardsPerSeatForDeal);

    if (prefersReduced) {
      let releaseDeal = () => {};
      try {
        releaseDeal = typeof onDealFxLock === 'function' ? onDealFxLock() : () => {};
      } catch {
        releaseDeal = () => {};
      }
      dealReleaseRef = releaseDeal;
      const flashes = Math.min(32, totalDealCards * 4);
      setSeatDealSlotRevealed?.(null);
      for (let i = 0; i < flashes; i += 1) {
        window.setTimeout(() => playDealSound(), i * 42);
      }
      queueMicrotask(() => {
        try {
          if (gen !== dealAnimGenRef.current) return;
          setSeatDealSlotRevealed?.(null);
          setRoundDealAnimActive?.(false);
        } finally {
          try {
            releaseDeal();
          } catch {
            /* ignore */
          }
          dealReleaseRef = null;
        }
      });
      return () => {
        cancelled = true;
        dealFlightAbortRef.current = true;
        try {
          dealReleaseRef?.();
        } catch {
          /* ignore */
        }
        dealReleaseRef = null;
        setRoundDealAnimActive?.(false);
        setSeatDealSlotRevealed?.(null);
      };
    }

    setSeatDealSlotRevealed?.(
      Array.from({ length: 4 }, () => Array.from({ length: REVEAL_SLOTS_PER_SEAT }, () => false)),
    );

    const run = async () => {
      let releaseDeal = () => {};
      try {
        try {
          releaseDeal = typeof onDealFxLock === 'function' ? onDealFxLock() : () => {};
        } catch {
          releaseDeal = () => {};
        }
        dealReleaseRef = releaseDeal;

        await new Promise((r) => {
          requestAnimationFrame(() => requestAnimationFrame(r));
        });
        if (cancelled || gen !== dealAnimGenRef.current) return;

        const availableSeats = Array.from(
          new Set(
            Object.keys(seatRefs?.current || {})
              .map((k) => Number(k))
              .filter((n) => Number.isInteger(n) && n >= 0 && n <= 3),
          ),
        ).sort((a, b) => a - b);
        const seatOrder = availableSeats.length > 0 ? availableSeats : DEAL_SEAT_ORDER;

        const deckEl = deckRef?.current;
        if (!deckEl) {
          const flashes = Math.min(32, totalDealCards * 4);
          for (let k = 0; k < flashes; k += 1) {
            if (cancelled || gen !== dealAnimGenRef.current) break;
            playDealSound();
            await sleep(42);
          }
          return;
        }

        for (let si = 0; si < seatOrder.length; si += 1) {
          const seat = seatOrder[si];
          if (cancelled || gen !== dealAnimGenRef.current || dealFlightAbortRef.current) break;
          for (let c = 0; c < totalDealCards; c += 1) {
            if (cancelled || gen !== dealAnimGenRef.current || dealFlightAbortRef.current) break;
            const seatEl = seatRefs?.current?.[seat];
            const targetEl =
              seat === heroSeat
                ? handSlotRefs?.[c]?.current
                : seatEl ?? seatCardSlotRefs?.[seat]?.[c]?.current;
            const cardSize = seat === heroSeat ? 'md' : 'sm';
            if (!targetEl) {
              await sleep(DEAL_HERO_STAGGER_MS);
              continue;
            }
            try {
              await flyHeroDealCardFromDeck(deckEl, targetEl, playDealSound, cardSize);
            } catch {
              /* ignore */
            }
            if (!cancelled && gen === dealAnimGenRef.current && !dealFlightAbortRef.current) {
              setSeatDealSlotRevealed?.((prev) => {
                if (!prev) return prev;
                const next = prev.map((row) => [...row]);
                if (next[seat]) next[seat][c] = true;
                return next;
              });
            }
            if (c < totalDealCards - 1) await sleep(DEAL_HERO_STAGGER_MS);
          }
          if (si < seatOrder.length - 1) await sleep(DEAL_BETWEEN_SEATS_MS);
        }
      } finally {
        try {
          releaseDeal();
        } catch {
          /* ignore */
        }
        dealReleaseRef = null;
        if (gen === dealAnimGenRef.current) {
          setSeatDealSlotRevealed?.(null);
          setRoundDealAnimActive?.(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      dealFlightAbortRef.current = true;
      try {
        dealReleaseRef?.();
      } catch {
        /* ignore */
      }
      dealReleaseRef = null;
      setSeatDealSlotRevealed?.(null);
    };
  }, [
    heroDealTriggerKey,
    suppressDealAnimation,
    cardsPerSeatForDeal,
    deckRef,
    seatRefs,
    handSlotRefs,
    seatCardSlotRefs,
    play,
    heroSeat,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    playDealSound,
    onDealFxLock,
  ]);
}
